import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ButtonExtendedProps, Form, ResponsiveContext } from 'grommet';
import { List, Validate } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import {
  ApiError,
  ClientService,
  EquipmentCategoryService,
  EquipmentService,
  FSERegistrationStatusService,
  FacilityService,
} from '/src/api';
import { ChargingNetworkService } from '/src/api/services/ChargingNetworkService';
import { ChargingTypeService } from '/src/api/services/ChargingTypeService';
import { EquipmentUseCaseService } from '/src/api/services/EquipmentUseCaseService';
import { ForkliftClassificationService } from '/src/api/services/ForkliftClassificationService';
import {
  AddButton,
  Box,
  BreadcrumbNav,
  DeleteEquipmentModal,
  EquipmentUsagesList,
  FirstActivePeriodSelect,
  FormCard,
  FormCardSection,
  FormPage,
  InfoBox,
  Input,
  Line,
  Link,
  RetireEquipmentModal,
  SaveButton,
  Select,
  Text,
} from '/src/components';
import { config } from '/src/config';
import { useEquipmentStore, useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import {
  Equipment,
  EquipmentCategory,
  FSERegistrationStatus,
  Facility,
  TEquipmentId,
  TFacilityId,
  hiddenEquipmentTypes,
  isFciCategory,
} from '/src/lib/models';
import { ChargingNetworks } from '/src/lib/models/ChargingNetwork';
import { ChargingType } from '/src/lib/models/ChargingType';
import { EquipmentUseCase } from '/src/lib/models/EquipmentUseCase';
import { ForkliftClassification } from '/src/lib/models/ForkliftClassification';
import { toastMessages } from '/src/lib/toast';
import { CountryId, DateString, FSERegistrationStatusIds, SelectOptions, TResponseMetadata } from '/src/lib/types';
import { PageNotFound } from '/src/pages';
import {
  equipmentIsRetired,
  facilityIsOrWa,
  getFacilityLabel,
  getFormattedDateString,
  getPageTitle,
  getPeriodFromDateString,
  getQuarterFromDate,
  pxToRem,
} from '/src/utils';
import { equipmentDetailsTour } from '/src/utils/productTours';

export type TMenuButtonItems = ButtonExtendedProps[] | ButtonExtendedProps[][];

export const EquipmentDetailsPage = observer(() => {
  /*** Context ***/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const equipmentStore = useEquipmentStore();
  const screenSize = useContext(ResponsiveContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /* Product Tour */
  useProductTour({
    tourName: 'equipment-details-tour',
    steps: equipmentDetailsTour,
    autoStart: true,
    showOnce: true,
  });

  /** URL Params **/
  const params = useParams();
  const clientId = parseInt(params.client_id ?? '');
  const equipmentId = parseInt(params.equipment_id ?? '');

  /** State **/
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isRetired, setIsRetired] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility>();
  const [currentFacilityPage, setCurrentFacilityPage] = useState(1);
  const [menuButtonItems, setMenuButtonItems] = useState<TMenuButtonItems>([]);
  const [originalFormValues, setOriginalFormValues] = useState<typeof equipmentStore.formValues>();

  const [categories, setCategories] = useState<EquipmentCategory[]>();
  const [fseRegistrationStatuses, setFSERegistrationStatuses] = useState<FSERegistrationStatus[]>();
  const [chargingNetworks, setChargingNetworks] = useState<ChargingNetworks[]>();
  const [equipmentUseCases, setEquipmentUseCases] = useState<EquipmentUseCase[]>();
  const [chargingTypes, setChargingTypes] = useState<ChargingType[]>();
  const [forkliftClassification, setforkliftClassification] = useState<ForkliftClassification[]>();
  const [clientName, setClientName] = useState('');
  const [isDemoClient, setIsDemoClient] = useState(false);
  const [equipment, setEquipment] = useState<Equipment>();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [facilityOptions, setFacilityOptions] = useState<SelectOptions<TFacilityId>>();
  const [facilityMetadata, setFacilityMetadata] = useState<TResponseMetadata>();
  const [isCanada, setIsCanada] = useState(false);
  const [isInvalidParams, setIsInvalidParams] = useState(false);
  const [previousFseId, setPreviousFseId] = useState<string>('');
  const [previousFseRu, setPreviousFseRu] = useState<string>('');
  /** Computed **/
  const isMobile = screenSize === 'small' || screenSize === 'xsmall';
  const isOrWa = facilityIsOrWa(selectedFacility);
  const isPending = equipment?.fse_registration_status.id === FSERegistrationStatusIds['Pending Client Information'];
  const isFinalized = !!equipment?.is_finalized;
  const isEditable =
    !isRetired &&
    ((userStore.isInternalUser && equipment?.is_finalized === false) || (userStore.isExternalUser && isPending));
  const isMetered = equipmentStore.formValues.is_metered;

  const pageTitle =
    equipment?.name ||
    (equipment?.manufacturer ? `${equipment?.manufacturer}: ${equipment?.serial_number}` : equipment?.serial_number) ||
    '';

  const currentEquipmentTypeId = equipmentStore.formValues.equipment_type_id;
  const equipmentTypeOptions =
    categories
      ?.find((category) => category.id === equipmentStore.formValues.equipment_category_id)
      ?.equipment_types?.filter((type) => !hiddenEquipmentTypes.has(type.id) || type.id === currentEquipmentTypeId)
      ?.map((type) => ({
        label: type.name,
        value: type.id,
      })) ?? [];

  // const [firstDayInServiceRange, setFirstDayInServiceRange] = useState<[start: DateString, end: DateString]>();

  const firstDayInServiceRange = useMemo(() => {
    if (!selectedFacility || !equipmentStore.formValues.first_active_reporting_quarter) return;
    const start = dayjs(selectedFacility.first_active_reporting_quarter).toISOString();
    const end = dayjs(equipmentStore.formValues.first_active_reporting_quarter)
      .add(2, 'months')
      .endOf('month')
      .toISOString();

    return [start, end] as [start: DateString, end: DateString];
  }, [selectedFacility, equipmentStore.formValues.first_active_reporting_quarter]);

  const lastReportingPeriod = equipment?.last_active_reporting_quarter
    ? isCanada
      ? equipment?.last_active_reporting_quarter?.slice(0, 4)
      : getQuarterFromDate(dayjs(equipment?.last_active_reporting_quarter).toDate())
    : undefined;

  const hasChanges = useMemo(() => {
    if (!originalFormValues) return false;
    return Object.keys(equipmentStore.formValues).some((key) => {
      const original = originalFormValues[key as keyof typeof originalFormValues];
      const current = equipmentStore.formValues[key as keyof typeof equipmentStore.formValues];
      return original !== current;
    });
  }, [originalFormValues, equipmentStore.formValues]);

  const titleBar = useMemo(() => {
    const badges: ReactNode[] = [];
    if (lastReportingPeriod) {
      badges.push(
        <InfoBox key="retired" pad={{ horizontal: '0.5rem' }} gap="xsmall">
          <Text size="0.95rem" weight={600} toUpperCase>
            {`${t('common:retired')}: ${lastReportingPeriod}`}
          </Text>
        </InfoBox>
      );
    }

    if (isFinalized) {
      badges.push(
        <InfoBox key="finalized" pad={{ horizontal: '0.5rem' }} gap="xsmall">
          <Validate size="16px" />
          <Text size="0.95rem" weight={600} toUpperCase>
            {t('common:finalized')}
          </Text>
        </InfoBox>
      );
    }
    if (isDemoClient) {
      badges.push(
        <InfoBox
          key="demo"
          pad={{ horizontal: '0.5rem' }}
          gap="xsmall"
          background={{ color: 'status-critical', opacity: 'medium' }}
          border={{ color: 'status-critical' }}
        >
          <Text size="0.95rem" weight={600} toUpperCase>
            Demo Client
          </Text>
        </InfoBox>
      );
    }

    return (
      <Box gap="small" direction="row">
        {badges}
      </Box>
    );
  }, [isFinalized, lastReportingPeriod, t, isDemoClient]);

  /** Methods **/
  const fetchPageData = async (equipmentId: TEquipmentId) => {
    setIsLoading(true);
    setEquipment(undefined);
    try {
      const [
        equipment,
        { data: facilitiesData, meta: facilitiesMeta },
        fseRegistrationStatuses,
        chargingNetworks,
        forkliftClassification,
        equipmentUseCases,
        chargingTypes,
      ] = await Promise.all([
        EquipmentService.get({ id: equipmentId }),
        FacilityService.list({ client_id: clientId, page: currentFacilityPage, limit: 1000 }),
        FSERegistrationStatusService.listFSERegistrationStatuses(),
        ChargingNetworkService.listChargingNetworks(),
        ForkliftClassificationService.listForkliftClassifications(),
        EquipmentUseCaseService.listEquipmentUseCases(),
        ChargingTypeService.listChargingTypes(),
      ]);

      if (equipment.client_id !== clientId) {
        setIsInvalidParams(true);
        return;
      }

      const client =
        userStore.isClientUser || userStore.isFacilityUser
          ? userStore.user?.clients.find((client) => client.id === clientId)
          : await ClientService.get({ id: clientId });

      if (client) {
        setClientName(client.name);
        setIsCanada(client.country_id === CountryId.Canada);
        setIsDemoClient(client.is_demo || false);
      }

      setEquipment(equipment);

      const isRetired = equipmentIsRetired(equipment);
      setIsRetired(isRetired);

      const userCanRetire =
        userStore.isClientAdmin || userStore.isFacilityAdmin
          ? !!userStore.user?.facilities.find((f) => f.id === equipment?.facility_id)
          : userStore.isFuseAdmin;
      const userCanDelete = userStore.isFuseAdmin;

      const menuItems: ButtonExtendedProps[] = [];
      if (!isRetired) {
        if (userCanRetire) {
          menuItems.push({ label: t('equipment:retireEquipment'), onClick: () => setShowRetireModal(true) });
        }
        if (!equipment?.is_finalized && userCanDelete) {
          menuItems.push({ label: t('equipment:deleteEquipment'), onClick: () => setShowDeleteModal(true) });
        }
      }

      setMenuButtonItems(menuItems);

      setFacilities(facilitiesData);
      setFacilityMetadata(facilitiesMeta);
      setFacilityOptions([
        ...(facilityOptions ?? []),
        ...facilitiesData.map((facility) => ({ label: getFacilityLabel(facility, true), value: facility.id })),
      ]);
      setSelectedFacility(equipment.facility);
      const programId = equipment.facility?.program?.id;

      setFSERegistrationStatuses(fseRegistrationStatuses);
      setChargingNetworks(chargingNetworks);
      setforkliftClassification(forkliftClassification);
      setEquipmentUseCases(equipmentUseCases);
      setChargingTypes(chargingTypes);

      const categories = await EquipmentCategoryService.listEquipmentCategories({ program_id: programId });
      setCategories(categories);

      const populatedFormValues = {
        ...equipmentStore.formValues,
        facility_id: equipment.facility_id ?? NaN,
        category_name: equipment.equipment_type.equipment_category?.name ?? '',
        equipment_type_id: equipment.equipment_type_id ?? NaN,
        equipment_category_id: equipment.equipment_type.equipment_category_id ?? NaN,
        unit_number: equipment.unit_number ?? null,
        serial_number: equipment.serial_number ?? '',
        manufacturer: equipment.manufacturer ?? '',
        model_number: equipment.model_number ?? '',
        model_year: equipment.model_year?.toString() ?? '',
        is_metered: equipment.is_metered,
        first_day_in_service: equipment.first_day_in_service,
        first_active_reporting_quarter: equipment.first_active_reporting_quarter,
        last_active_reporting_quarter: equipment.last_active_reporting_quarter,
        latitude: equipment.latitude?.toString() ?? '',
        longitude: equipment.longitude?.toString() ?? '',
        fse_registration_status_id: equipment.fse_registration_status_id ?? NaN,
        fse_id: equipment.fse_id ?? '',
        fse_ru: equipment.fse_ru ?? '',
        comments: equipment.comments ?? '',
        charging_network_id: equipment.charging_network_id ?? '',
        forklift_classification_id: equipment.forklift_classification_id ?? null,
        equipment_use_case_id: equipment.equipment_use_case_id ?? '',
        charging_type_id: equipment.charging_type_id ?? '',
        nameplate_rating_kw: equipment.nameplate_rating_kw ?? null,
      };
      equipmentStore.setFormValues(populatedFormValues);
      setOriginalFormValues(populatedFormValues);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.generic.error);
    } finally {
      setIsLoading(false);
    }
  };

  const nextFacilityPage = async () => {
    if (isLoadingFacilities || (facilityMetadata && currentFacilityPage + 1 > facilityMetadata.last_page)) return;
    setIsLoadingFacilities(true);
    try {
      setCurrentFacilityPage(currentFacilityPage + 1);
      const { meta, data } = await FacilityService.list({
        client_id: clientId,
        page: currentFacilityPage + 1,
      });
      setFacilities([...facilities, ...data]);
      setFacilityOptions([
        ...(facilityOptions ?? []),
        ...data.map((facility) => ({ label: getFacilityLabel(facility), value: facility.id })),
      ]);
      setFacilityMetadata(meta);
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchEquipment.error);
    } finally {
      setIsLoadingFacilities(false);
    }
  };

  const updateEquipment = async () => {
    if (!equipment || !userStore.user) return;

    if (
      !isFinalized &&
      selectedFacility?.program?.id === 1 &&
      equipmentStore.formValues.equipment_category_id === 5 &&
      !equipmentStore.formValues.forklift_classification_id
    ) {
      equipmentStore.formErrors['forklift_classification_id'] = 'Forklift Classification is required.';
      return;
    } else {
      delete equipmentStore.formErrors['forklift_classification_id'];
    }

    try {
      setIsUpdating(true);

      let updatedEquipment: Equipment | undefined;

      if (userStore.isInternalUser) {
        updatedEquipment = isFinalized
          ? await equipmentStore.updateFinalizedInternal(equipment.id)
          : await equipmentStore.updateNonFinalizedInternal(equipment);
      } else if (userStore.isExternalUser) {
        updatedEquipment = isPending
          ? await equipmentStore.updatePendingExternal(equipment)
          : await equipmentStore.updateNonPendingExternal(equipment.id);
      }

      if (updatedEquipment) {
        setEquipment(updatedEquipment);
        toast.success(toastMessages.updateEquipment.success);
        setShouldRefresh(true);
      }
    } catch (err) {
      const error = err as ApiError;
      let showToast = true;
      if (error?.status === 400) {
        const body = (error.body ?? {}) as Record<string, string>;
        if (body?.message?.toLowerCase().includes('first active reporting quarter')) {
          equipmentStore.formErrors['first_active_reporting_quarter'] = body.message;
          showToast = false;
        }
      }
      if (showToast) globalStore.handleApiError(error, toastMessages.updateEquipment.error);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    equipmentStore.resetForm();
  }, []);

  useEffect(() => {
    if (equipment) {
      document.title = getPageTitle(
        config.client.basePageTitle,
        equipment?.name ?? `${equipment?.manufacturer}: ${equipment?.serial_number}`
      );
    }
  }, [equipment]);

  useEffect(() => {
    if (equipmentId) fetchPageData(equipmentId);
  }, [equipmentId]);

  useEffect(() => {
    if (equipmentId && shouldRefresh) {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      fetchPageData(equipmentId).finally(() => setShouldRefresh(false));
    }
  }, [equipmentId, shouldRefresh]);

  // Change Category
  useEffect(() => {
    if (!categories) return;

    const category = categories.find(({ name }) => name === equipmentStore.formValues.category_name);
    const newCategoryId = category?.id;
    equipmentStore.updateFormValue('equipment_category_id', newCategoryId);

    const equipmentTypes =
      category?.equipment_types?.filter(
        (type) => !hiddenEquipmentTypes.has(type.id) || type.id === equipmentStore.formValues.equipment_type_id
      ) || [];
    const validTypeIds = equipmentTypes.map((type) => type.id);

    if (equipmentTypes.length === 1) {
      equipmentStore.updateFormValue('equipment_type_id', equipmentTypes[0].id);
    } else if (!validTypeIds.includes(equipmentStore.formValues.equipment_type_id as number)) {
      equipmentStore.updateFormValue('equipment_type_id', undefined);
    }

    if (!isFciCategory(newCategoryId)) {
      equipmentStore.updateFormValue('nameplate_rating_kw', null);
    }

    if (newCategoryId !== 5) {
      equipmentStore.updateFormValue('forklift_classification_id', null);
    }
  }, [categories, equipmentStore.formValues?.category_name]);

  // Change Facility
  useEffect(() => {
    if (equipmentStore.formValues.facility_id && selectedFacility?.id !== equipmentStore.formValues.facility_id) {
      const facility = facilities.find((facility) => facility.id === equipmentStore.formValues.facility_id);
      setSelectedFacility(facility);
    }
  }, [equipmentStore.formValues.facility_id, selectedFacility, facilities]);

  // 404 Redirect
  useEffect(() => {
    if (isLoading) return;
    if (equipment && equipment.id !== equipmentId) return;
    setIsInvalidParams(Number.isNaN(clientId) || Number.isNaN(equipmentId) || equipment?.id === undefined);
  }, [clientId, equipmentId, equipment, isLoading]);

  /** Render **/
  const renderFirstReportingPeriod = () => {
    const firstReportingPeriod = equipmentStore.formValues['first_active_reporting_quarter'] ?? '';
    const value = isEditable ? firstReportingPeriod : getPeriodFromDateString(firstReportingPeriod, isCanada);

    return (
      <FirstActivePeriodSelect
        id="first_active_reporting_quarter"
        label={t('common:firstReportingPeriod')}
        placeholder="Select Period..."
        value={value}
        setValue={(value) => equipmentStore.updateFormValue('first_active_reporting_quarter', value)}
        clientId={equipment?.client_id}
        programId={selectedFacility?.program?.id}
        reportingPeriodTypeId={equipment?.reporting_period_type_id}
        firstActivePeriod={selectedFacility?.first_active_reporting_quarter}
        error={equipmentStore.formErrors['first_active_reporting_quarter']}
        disabled={!isEditable}
        required={isEditable}
        fill="horizontal"
        data-tour="reporting-period-selector"
      />
    );
  };

  const renderFirstDayInService = () => {
    const firstDayInService = equipmentStore.formValues.first_day_in_service ?? '';
    const value = isEditable ? firstDayInService : getFormattedDateString(firstDayInService, isCanada);

    return (
      <Input
        id="first_day_in_service"
        label="First Day in Service"
        fill="horizontal"
        value={value}
        dateBounds={firstDayInServiceRange}
        setValue={(value) => equipmentStore.updateFormValue('first_day_in_service', value)}
        error={equipmentStore.formErrors['first_day_in_service']}
        onSubmit={updateEquipment}
        disabled={!isEditable}
        componentType="date"
        required={isOrWa && isEditable}
      />
    );
  };

  return isInvalidParams ? (
    <PageNotFound />
  ) : (
    <FormPage
      title={pageTitle}
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            { name: 'Clients', link: !userStore.isClientUser && !userStore.isFacilityUser ? '/clients' : undefined },
            { name: clientName, link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
            { name: 'Facilities', link: `/clients/${clientId}/facilities` },
            {
              name: getFacilityLabel(selectedFacility),
              link: `/clients/${clientId}/facilities/${selectedFacility?.id ?? ''}`,
            },
            { name: 'Equipment', link: `/clients/${clientId}/facilities/${selectedFacility?.id ?? ''}/equipment` },
          ]}
          currentPageName={pageTitle}
        />
      }
      titleBar={titleBar}
      isLoading={isLoading}
      isDetailPage
      hideMenuButton={!isLoading && equipment && !menuButtonItems.length}
      menuButtonItems={menuButtonItems}
    >
      <FormCard
        title={t('equipment:equipmentDetails')}
        icon={<List size="24px" color="brand" />}
        onSubmit={updateEquipment}
        isLoading={isLoading || isUpdating}
        hideSaveButton
        saveButtonLabel={t('common:saveChanges')}
        data-tour="equipment-info"
        headerChildren={[
          <AddButton
            key="return-to-facility"
            label={t('equipment:returnToFacility')}
            targetUrl={`/clients/${clientId}/facilities/${selectedFacility?.id}`}
          />,
        ]}
        saveButton={
          <Box direction="row" gap="medium" justify="end">
            <SaveButton
              type="submit"
              label={t('common:saveChanges')}
              loadingLabel="SAVING..."
              isLoading={isLoading || isUpdating}
              onClick={updateEquipment}
              disabled={!userStore.isAdminUser || !hasChanges}
            />
            {!isRetired && (
              <SaveButton
                label={t('equipment:addNewEquipment')}
                onClick={() =>
                  navigate(`/clients/${clientId}/equipment/create?category=&facility_id=${selectedFacility?.id}`)
                }
                background="neutral-1"
                hoverBackground="accent-7"
              />
            )}
          </Box>
        }
      >
        <Form>
          <Box direction={isMobile ? 'column' : 'row'} margin={{ bottom: '1rem' }} gap="1rem">
            <Box width={isMobile ? '100%' : '60%'} gap="1rem">
              <FormCardSection row>
                <Select
                  id="category_name"
                  label={t('equipment:equipmentCategory')}
                  value={equipmentStore.formValues.category_name}
                  setValue={(value) => equipmentStore.updateFormValue('category_name', value)}
                  options={categories?.map((category) => category.name) ?? []}
                  error={equipmentStore.formErrors['category_name']}
                  disabled={!isEditable}
                  required
                  fill="horizontal"
                />
                <Select
                  id="equipment_type_id"
                  label={t('equipment:equipmentType')}
                  placeholder="Choose..."
                  value={
                    equipmentTypeOptions.length === 1 && !equipmentStore.formValues.equipment_type_id
                      ? equipmentTypeOptions[0].value
                      : equipmentStore.formValues.equipment_type_id
                  }
                  setValue={(value) => equipmentStore.updateFormValue('equipment_type_id', value)}
                  options={equipmentTypeOptions}
                  fill="horizontal"
                  error={equipmentStore.formErrors['equipment_type_id']}
                  required
                  disabled={!userStore.isAdminUser || !isEditable}
                />
                {selectedFacility?.program?.id === 1 && equipmentStore.formValues.equipment_category_id === 5 && (
                  <Select
                    id="forklift_classification"
                    label={t('equipment:forkliftClassification')}
                    placeholder="Choose..."
                    value={equipmentStore.formValues.forklift_classification_id}
                    setValue={(value) => {
                      equipmentStore.updateFormValue('forklift_classification_id', value);
                    }}
                    options={(forkliftClassification ?? []).map((s) => ({ label: s.name, value: s.id }))}
                    error={equipmentStore.formErrors['forklift_classification_id']}
                    disabled={!isEditable || isCanada}
                    required
                    fill="horizontal"
                  />
                )}
                {isFciCategory(equipmentStore.formValues.equipment_category_id) && (
                  <Input
                    id="nameplate_rating_kw"
                    label={t('equipment:nameplateRating')}
                    value={equipmentStore.formValues.nameplate_rating_kw ?? ''}
                    setValue={(value) => equipmentStore.updateFormValue('nameplate_rating_kw', value)}
                    error={equipmentStore.formErrors['nameplate_rating_kw']}
                    onSubmit={updateEquipment}
                    fill="horizontal"
                    type="number"
                    disabled={!isEditable || isCanada}
                    required
                  />
                )}
              </FormCardSection>
              <Line margin="0.5rem" />
              <FormCardSection>
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="serial_number"
                    label={t('equipment:serialNumber')}
                    value={equipmentStore.formValues.serial_number}
                    setValue={(value) => equipmentStore.updateFormValue('serial_number', value)}
                    error={equipmentStore.formErrors['serial_number']}
                    onSubmit={updateEquipment}
                    fill="horizontal"
                    required
                    disabled={!userStore.isAdminUser || !isEditable}
                  />
                  <Input
                    id="unit_number"
                    label={t('equipment:unitNumber')}
                    value={equipmentStore.formValues.unit_number ?? ''}
                    setValue={(value) => equipmentStore.updateFormValue('unit_number', value)}
                    error={equipmentStore.formErrors['unit_number']}
                    onSubmit={updateEquipment}
                    fill="horizontal"
                    disabled={!userStore.isAdminUser && !isEditable}
                  />
                </Box>
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem" margin={{ top: 'small' }}>
                  <Input
                    id="model_year"
                    label={t('equipment:modelYear')}
                    value={equipmentStore.formValues.model_year}
                    setValue={(value) => equipmentStore.updateFormValue('model_year', value)}
                    error={equipmentStore.formErrors['model_year']}
                    onSubmit={updateEquipment}
                    type="number"
                    fill="horizontal"
                    required
                    disabled={!userStore.isAdminUser || !isEditable}
                  />
                  <Input
                    id="manufacturer"
                    label={t('equipment:manufacturer')}
                    value={equipmentStore.formValues.manufacturer}
                    setValue={(value) => equipmentStore.updateFormValue('manufacturer', value)}
                    error={equipmentStore.formErrors['manufacturer']}
                    onSubmit={updateEquipment}
                    fill="horizontal"
                    required
                    disabled={!userStore.isAdminUser || !isEditable}
                  />
                  <Input
                    id="model_number"
                    label={t('equipment:modelNumber')}
                    value={equipmentStore.formValues.model_number}
                    setValue={(value) => equipmentStore.updateFormValue('model_number', value)}
                    error={equipmentStore.formErrors['model_number']}
                    onSubmit={updateEquipment}
                    fill="horizontal"
                    disabled={!userStore.isAdminUser || !isEditable}
                  />
                </Box>
              </FormCardSection>
              <Line margin="xsmall" />

              <FormCardSection>
                <Box direction="row" gap="1rem">
                  <Select
                    id="is_metered"
                    label={t('equipment:metered')}
                    value={equipmentStore.formValues.is_metered ? 'Yes' : 'No'}
                    setValue={(value) => equipmentStore.updateFormValue('is_metered', value === 'Yes')}
                    options={['Yes', 'No']}
                    error={equipmentStore.formErrors['is_metered']}
                    disabled={!isEditable || isCanada}
                    required
                    fill="horizontal"
                  />

                  {isMetered && !(isOrWa && !isRetired) && !(!isOrWa && isRetired) && !(isOrWa && isRetired) && (
                    <Select
                      id="charging_network"
                      label={t('equipment:chargingNetwork')}
                      placeholder="Choose..."
                      value={equipmentStore.formValues.charging_network_id}
                      setValue={(value) => {
                        equipmentStore.updateFormValue('charging_network_id', value);
                      }}
                      options={(chargingNetworks ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['charging_network_id']}
                      disabled={!isEditable}
                      required
                      fill="horizontal"
                    />
                  )}

                  {renderFirstReportingPeriod()}
                </Box>

                {(selectedFacility?.program?.id === 4 || selectedFacility?.program?.id === 5) && (
                  <Box direction={isMobile ? 'column' : 'row'} gap="1rem" margin={{ top: 'small' }}>
                    <Select
                      id="equipment_use_case"
                      label={t('equipment:equipmentUseCase')}
                      placeholder="Choose..."
                      value={equipmentStore.formValues.equipment_use_case_id}
                      setValue={(value) => equipmentStore.updateFormValue('equipment_use_case_id', value)}
                      options={(equipmentUseCases ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['equipment_use_case_id']}
                      disabled={!userStore.isAdminUser || !isEditable}
                      required
                      flex
                    />
                    <Select
                      id="charging_type"
                      label={t('equipment:chargingType')}
                      placeholder="Choose..."
                      value={equipmentStore.formValues.charging_type_id}
                      setValue={(value) => equipmentStore.updateFormValue('charging_type_id', value)}
                      options={(chargingTypes ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['charging_type_id']}
                      disabled={!userStore.isAdminUser || !isEditable}
                      required
                      flex
                    />
                  </Box>
                )}

                {/* Second row - only appears when we need to show date fields */}
                {(isOrWa || (isRetired && isMetered)) && (
                  <Box direction="row" gap="1rem" margin={{ top: 'small' }}>
                    {isMetered && (
                      <Select
                        id="charging_network"
                        label={t('equipment:chargingNetwork')}
                        placeholder="Choose..."
                        value={equipmentStore.formValues.charging_network_id}
                        setValue={(value) => {
                          equipmentStore.updateFormValue('charging_network_id', value);
                        }}
                        options={(chargingNetworks ?? []).map((s) => ({ label: s.name, value: s.id }))}
                        error={equipmentStore.formErrors['charging_network_id']}
                        disabled={!isEditable}
                        required
                        fill="horizontal"
                      />
                    )}

                    {(isOrWa && !isRetired) || (isOrWa && isRetired) ? renderFirstDayInService() : null}
                  </Box>
                )}
              </FormCardSection>
              <Line margin="0.5rem" />
              {isRetired && (
                <Input
                  name="equipment-retirement_reason"
                  label={t('common:reasonForRetiring')}
                  placeholder="-"
                  value={equipment?.retirement_reason_other || equipment?.retirement_reason?.name || ''}
                  disabled
                  componentType="textArea"
                  opacity={1}
                />
              )}
              <Box flex="grow">
                <Input
                  id="comments"
                  label={t('common:comments')}
                  value={equipmentStore.formValues.comments ?? ''}
                  setValue={(value) => equipmentStore.updateFormValue('comments', value)}
                  error={equipmentStore.formErrors['comments']}
                  onSubmit={updateEquipment}
                  componentType="textArea"
                  centerLabel={false}
                  height="12rem"
                  disabled={!userStore.isAdminUser && !isEditable}
                />
              </Box>
            </Box>
            {!isMobile && <Line direction="vertical" margin="3rem" />}
            <Box gap="1rem" width={isMobile ? '100%' : '40%'}>
              {!isMobile && (
                <>
                  <FormCardSection>
                    <Select
                      id="facility_id"
                      label={t('common:facility')}
                      value={equipmentStore.formValues.facility_id}
                      setValue={(value) => equipmentStore.updateFormValue('facility_id', value)}
                      onMore={nextFacilityPage}
                      options={facilityOptions ?? []}
                      error={equipmentStore.formErrors['facility_id']}
                      required
                      disabled
                    />
                  </FormCardSection>
                  {userStore.isInternalUser && <Line margin="0.5rem" />}
                </>
              )}
              {isMobile && userStore.isInternalUser && <Line margin="0.5rem" />}
              {userStore.isInternalUser && (
                <FormCardSection title="FSE" data-tour="equipment-status">
                  <Box gap="1rem">
                    <Select
                      label={t('equipment:fseRegistrationStatus')}
                      id="fse_registration_status_id"
                      placeholder="Choose..."
                      value={equipmentStore.formValues.fse_registration_status_id}
                      setValue={(value) => {
                        if (value !== FSERegistrationStatusIds.Approved && equipmentStore.isApproved) {
                          setPreviousFseId(equipmentStore.formValues.fse_id ?? '');
                          equipmentStore.updateFormValue('fse_id', '');
                        } else if (value === FSERegistrationStatusIds.Approved && previousFseId) {
                          equipmentStore.updateFormValue('fse_id', previousFseId);
                        }

                        if (value === FSERegistrationStatusIds['Pending Client Information']) {
                          setPreviousFseRu(equipmentStore.formValues.fse_ru ?? '');
                          equipmentStore.updateFormValue('fse_ru', '');
                        } else if (previousFseRu) {
                          equipmentStore.updateFormValue('fse_ru', previousFseRu);
                        }

                        equipmentStore.updateFormValue('fse_registration_status_id', value);
                      }}
                      options={(fseRegistrationStatuses ?? []).map((s) => ({ label: s.name, value: s.id }))}
                      error={equipmentStore.formErrors['fse_registration_status_id']}
                      required
                      disabled={!userStore.isAdminUser || !isEditable}
                    />
                    <Input
                      id="fse_id"
                      label="FSE ID"
                      value={equipmentStore.formValues.fse_id ?? ''}
                      setValue={(value) => {
                        equipmentStore.updateFormValue('fse_id', value);
                        setPreviousFseId(value);
                      }}
                      error={equipmentStore.formErrors['fse_id']}
                      onSubmit={updateEquipment}
                      required={equipmentStore.isApproved}
                      disabled={!userStore.isAdminUser || !isEditable || !equipmentStore.isApproved}
                    />
                    <Input
                      id="fse_ru"
                      label="FSE RU"
                      value={equipmentStore.formValues.fse_ru ?? ''}
                      setValue={(value) => {
                        equipmentStore.updateFormValue('fse_ru', value);
                        setPreviousFseRu(value);
                      }}
                      error={equipmentStore.formErrors['fse_ru']}
                      onSubmit={updateEquipment}
                      disabled={
                        !userStore.isAdminUser ||
                        !isEditable ||
                        equipmentStore.formValues.fse_registration_status_id ===
                          FSERegistrationStatusIds['Pending Client Information']
                      }
                    />
                  </Box>
                </FormCardSection>
              )}
              <Line margin="0.5rem" />
              <FormCardSection title={t('equipment:location')}>
                {/* Facility moved under Location in Mobile */}
                {isMobile && (
                  <FormCardSection>
                    <Select
                      id="facility_id"
                      label={t('common:facility')}
                      value={equipmentStore.formValues.facility_id}
                      setValue={(value) => equipmentStore.updateFormValue('facility_id', value)}
                      onMore={nextFacilityPage}
                      options={facilityOptions ?? []}
                      error={equipmentStore.formErrors['facility_id']}
                      required
                      disabled
                    />
                  </FormCardSection>
                )}
                <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                  <Input
                    id="latitude"
                    label={t('equipment:latitude')}
                    value={equipmentStore.formValues.latitude}
                    type="number"
                    setValue={(value) => equipmentStore.updateFormValue('latitude', value)}
                    error={equipmentStore.formErrors['latitude']}
                    onSubmit={updateEquipment}
                    flex
                    placeholder="DDD.DDDDD°"
                    required
                    disabled={!isEditable || (userStore.isExternalUser && !isPending)}
                  />
                  <Input
                    id="longitude"
                    label={t('equipment:longitude')}
                    value={equipmentStore.formValues.longitude}
                    type="number"
                    setValue={(value) => equipmentStore.updateFormValue('longitude', value)}
                    error={equipmentStore.formErrors['longitude']}
                    onSubmit={updateEquipment}
                    flex
                    placeholder="DDD.DDDDD°"
                    required
                    disabled={!isEditable || (userStore.isExternalUser && !isPending)}
                  />
                </Box>
              </FormCardSection>
              {equipment?.created_by_user_id && <Line margin="0.5rem" />}
              {equipment?.created_by_user_id && (
                <FormCardSection title={t('equipment:creatorDetails')}>
                  <Box direction={isMobile ? 'column' : 'row'} gap="1rem">
                    <Box flex="grow">
                      <Box round="6px" background="light-6" height={pxToRem(58)} gap="xsmall">
                        <Box pad={{ top: 'xsmall', left: 'small' }}>
                          <Text size="small" color="accent-1">
                            {t('equipment:createdBy')}
                          </Text>
                        </Box>
                        <Link
                          to={`/users/${equipment?.created_by_user_id}`}
                          textDecoration="none"
                          style={{ paddingLeft: '12px', paddingBottom: pxToRem(10), width: 'fit-content' }}
                        >
                          <Text color="accent-1" fontFamily="Lato, sans-serif" weight="bold">
                            {equipment?.created_by_user?.name}
                          </Text>
                        </Link>
                      </Box>
                    </Box>
                    <Input
                      id="created_at"
                      label={t('equipment:createdAt')}
                      value={dayjs(equipment?.created_at).format('MM/DD/YYYY')}
                      setValue={() => {}}
                      flex
                      disabled
                    />
                  </Box>
                </FormCardSection>
              )}
            </Box>
          </Box>
        </Form>
      </FormCard>
      {equipment && (
        <Box id="equipment-usage-data" animation={[{ type: 'slideDown', duration: 300, size: 'xsmall' }]}>
          <EquipmentUsagesList equipment={equipment} />
        </Box>
      )}
      {showRetireModal && !!equipment && (
        <RetireEquipmentModal
          equipment={equipment}
          setIsVisible={setShowRetireModal}
          setShouldRefresh={setShouldRefresh}
          isCanada={isCanada}
        />
      )}
      {showDeleteModal && !!equipment && (
        <DeleteEquipmentModal equipment={equipment} setIsVisible={setShowDeleteModal} />
      )}
    </FormPage>
  );
});
